// SPDX-License-Identifier: SEE LICENSE
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ActivityLog } from '../components/ActivityLog';
import { CertificateCard } from '../components/CertificateCard';
import { Layout } from '../components/Layout';
import { PillarPanel } from '../components/PillarPanel';
import { PillarTile } from '../components/PillarTile';
import { PILLARS } from '../lib/pillars';
import { useScaleToFit } from '../lib/useScaleToFit';
import { useSessionStream } from '../lib/useSessionStream';
import { useSessionsStream } from '../lib/useSessionsStream';
import type { StepKind } from '../lib/types';

export function SessionView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const live = useSessionStream(id);
  // Initial pillar selection: respect ?pillar=<kind> query param (used
  // for verification screenshots and deep-link sharing). Falls back to
  // 'antenna' otherwise.
  const initialPillar = (() => {
    const p = new URLSearchParams(window.location.search).get('pillar');
    const valid: StepKind[] = ['antenna', 'battery_soh', 'battery_forecast', 'touch', 'oled', 'vibro', 'magneto', 'selfemit', 'ultrasonic', 'drop', 'acta', 'thermal', 'wipe'];
    return (p && valid.includes(p as StepKind)) ? (p as StepKind) : 'antenna';
  })();
  const [selected, setSelected] = useState<StepKind>(initialPillar);
  const { sessions } = useSessionsStream();

  // Auto-redirect to a newer session when the current page is stale.
  useEffect(() => {
    if (!live.session) return;
    const sorted = [...sessions].sort((a, b) => b.started_at - a.started_at);
    const currentStarted = live.session.startedAt ?? 0;
    const currentFinalized = live.session.finalizedAt != null;
    const lastFrameAgeMs = live.lastFrameAt ? Date.now() - live.lastFrameAt : Number.POSITIVE_INFINITY;
    const FIVE_MIN = 5 * 60 * 1000;
    const newer = sorted.find((s) => {
      if (s.id === id) return false;
      if (s.started_at <= currentStarted) return false;
      if (Date.now() - s.started_at > FIVE_MIN) return false;
      if ((s.active ?? 0) > 0) return true;
      return currentFinalized || lastFrameAgeMs > 20_000;
    });
    if (newer) {
      navigate(`/sessions/${newer.id}`, { replace: true });
    }
  }, [id, live.session?.startedAt, live.session?.finalizedAt, live.lastFrameAt, sessions, navigate]);

  const stepByKind = new Map((live.session?.steps ?? []).map((s) => [s.kind, s]));
  const selectedMeta = PILLARS.find((p) => p.kind === selected) ?? PILLARS[0]!;

  const SENTINEL = /no run recorded|awaiting|no capture|verdict withheld/i;
  const allSteps = live.session?.steps ?? [];
  const isPassReal = (s: (typeof allSteps)[number]) => {
    if (s.status !== 'passed') return false;
    const h = (s.summary as Record<string, unknown> | undefined)?.headline;
    if (typeof h !== 'string') return true;
    return !SENTINEL.test(h);
  };
  const passed = allSteps.filter(isPassReal).length;
  const concerns =
    allSteps.filter((s) => s.status === 'concern').length +
    allSteps.filter((s) => s.status === 'passed' && !isPassReal(s)).length;
  const failed = allSteps.filter((s) => s.status === 'failed').length;
  const activeSteps = allSteps.filter((s) => s.status === 'active');
  // Pillars that have either streamed real telemetry OR reached a terminal
  // verdict. Reflects "how many pillars have actually done work" — stays
  // > 0 after the chain completes so the metric isn't a misleading "0".
  const pillarsWithTelemetry = Object.keys(live.framesByKind).filter(
    (k) => (live.framesByKind[k]?.length ?? 0) > 0,
  );
  const pillarsTerminal = allSteps.filter((s) =>
    ['passed', 'concern', 'failed', 'skipped'].includes(s.status),
  );
  const activeKindsCount = Math.max(
    activeSteps.length,
    new Set([...pillarsWithTelemetry, ...pillarsTerminal.map((s) => s.kind)]).size,
  );
  const nonWipe = allSteps.filter((s) => s.kind !== 'wipe');
  const allDone =
    nonWipe.length > 0 &&
    nonWipe.every((s) => ['passed', 'concern', 'failed', 'skipped'].includes(s.status));

  const overall: 'PASS' | 'CONCERN' | 'FAIL' | 'INCOMPLETE' =
    failed > 0 ? 'FAIL' : concerns > 0 ? 'CONCERN' : passed === allSteps.length && allSteps.length > 0 ? 'PASS' : 'INCOMPLETE';
  const overallColorMap = { PASS: '#34d399', CONCERN: '#f5bf45', FAIL: '#f06a5e', INCOMPLETE: '#15ddc9' };
  const overallColor = overallColorMap[overall];

  // Auto-focus the pillar that's actively streaming — but DWELL on the
  // previously-selected pillar for a few seconds after it terminates,
  // so the operator gets to see the verdict instead of being yanked
  // away the instant the next pillar starts capturing.
  const RESULT_DWELL_MS = 4000;
  const dwellRef = useRef<{ kind: StepKind; at: number } | null>(null);
  // Track when the currently selected pillar enters a terminal state.
  useEffect(() => {
    const selStep = stepByKind.get(selected);
    if (!selStep) return;
    const TERMINAL: ReadonlyArray<string> = ['passed', 'concern', 'failed', 'skipped'];
    if (TERMINAL.includes(selStep.status)) {
      if (!dwellRef.current || dwellRef.current.kind !== selected) {
        dwellRef.current = { kind: selected, at: Date.now() };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, stepByKind.get(selected)?.status]);

  useEffect(() => {
    if (!live.activeKind || live.activeKind === selected) return;
    const activeStep = stepByKind.get(live.activeKind);
    if (activeStep?.status !== 'active') return;
    const dwell = dwellRef.current;
    const ageMs = dwell ? Date.now() - dwell.at : Number.POSITIVE_INFINITY;
    const remaining = Math.max(0, RESULT_DWELL_MS - ageMs);
    if (remaining === 0) {
      setSelected(live.activeKind as StepKind);
      return;
    }
    const timer = setTimeout(() => setSelected(live.activeKind as StepKind), remaining);
    return () => clearTimeout(timer);
  }, [live.activeKind, selected, stepByKind]);

  // Counters for the METRICS PANEL strip.
  const framesTotal = Object.values(live.framesByKind).reduce((s, a) => s + a.length, 0);
  // A verdict is "signed" once a pillar has reached a terminal status with
  // a real (non-sentinel) headline. Falls back to live SSE verdicts seen
  // mid-run so the counter rises live, not only after the chain completes.
  const signedFromSteps = allSteps.filter((s) => {
    if (!['passed', 'concern', 'failed'].includes(s.status)) return false;
    const h = (s.summary as Record<string, unknown> | undefined)?.headline;
    if (typeof h !== 'string') return false;
    return !SENTINEL.test(h);
  }).length;
  const verdictsSigned = Math.max(signedFromSteps, Object.keys(live.verdictByKind).length);
  const bytesEstimate = framesTotal * 168; // rough: average frame body
  const fmtBytes = (n: number) =>
    n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`;
  const uptime = live.session?.startedAt ? msToUptime(Date.now() - live.session.startedAt) : '—';
  const startedHHMMSS = live.session?.startedAt ? new Date(live.session.startedAt).toLocaleTimeString([], { hour12: false }) : '—';
  const clockNow = useTickingClock();

  // Fleet counts from the sessions list (open + total devices today).
  const openSessions = sessions.filter((s) => (s.active ?? 0) > 0 || !s.finalized_at).length;
  const deviceSet = new Set(sessions.map((s) => s.device_fingerprint).filter(Boolean));

  const sessionShort = id?.slice(0, 12) ?? '—';
  const startedAgo = live.session?.startedAt ? relAgo(live.session.startedAt) : '—';

  return (
    <Layout
      connected={live.connected}
      right={null}
      hideHeader={true}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
        {/* TOP NAV — back to sessions browser */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11.5,
              color: '#15ddc9',
              background: 'rgba(20,222,205,0.06)',
              border: '1px solid rgba(20,222,205,0.32)',
              borderRadius: 9,
              padding: '7px 13px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            All Sessions
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#9fb3b8', background: 'rgba(20,222,205,0.08)', borderRadius: 5, padding: '1px 6px', fontWeight: 600 }}>
              {sessions.length}
            </span>
          </button>
          <img
            src="/retronics-logo.jpeg"
            alt="Retronics"
            style={{ width: 34, height: 34, borderRadius: 9, objectFit: 'contain', border: '1px solid rgba(20,222,205,0.4)', background: 'rgba(20,222,205,0.07)', boxShadow: '0 0 18px -4px rgba(20,222,205,0.6)' }}
          />
        </div>

        {/* SESSION + STATUS */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <PhoneMock />
            <div>
              <div style={{ fontSize: 10, letterSpacing: 2, color: '#6f878e', fontWeight: 600, marginBottom: 3 }}>SESSION</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#f4faf9', letterSpacing: '-0.5px', lineHeight: 1.05 }}>
                {live.session?.deviceModel ?? 'Device health check'}
              </div>
              <div
                className="lde-mono"
                style={{ fontSize: 11.5, color: '#5d7178', marginTop: 8, fontFamily: "'JetBrains Mono', monospace" }}
              >
                {sessionShort}&nbsp;&nbsp;·&nbsp;&nbsp;started {startedAgo}
                {live.session?.deviceFingerprint ? <>&nbsp;&nbsp;·&nbsp;&nbsp;{live.session.deviceFingerprint}</> : null}
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid rgba(120,160,160,0.16)',
              borderRadius: 14,
              background: 'rgba(9,16,26,0.6)',
              padding: '12px 6px',
            }}
          >
            <StatBox value={passed} label="PASSED" color="#34d399" />
            <StatBox value={concerns} label="CONCERN" color="#f5bf45" />
            <StatBox value={failed} label="FAILED" color="#f06a5e" />
            <div style={{ padding: '0 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: '#6f878e', fontWeight: 600, marginBottom: 7 }}>OVERALL</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: overallColor, letterSpacing: 0.5 }}>{overall}</div>
            </div>
          </div>
        </div>

        {/* METRICS PANEL */}
        <div
          className="sv-metrics-grid"
          style={{
            border: '1px solid rgba(120,160,160,0.16)',
            borderRadius: 14,
            background: 'rgba(9,16,26,0.55)',
            padding: '15px 4px',
          }}
        >
          <div style={{ display: 'contents' }}>
            <Metric
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 8 12 12 15 14" /></svg>}
              label="SYSTEM CLOCK"
              value={clockNow}
              sub="local wall-clock · ms"
              mono
            />
            <Metric
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="8" /><path d="M5 3 2 6" /><path d="m22 6-3-3" /><polyline points="12 9 12 13 14 15" /></svg>}
              label="SESSION UPTIME"
              value={uptime}
              sub={`started ${startedHHMMSS}`}
              divider
            />
            <Metric
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>}
              label="FRAMES RECEIVED"
              value={framesTotal.toString()}
              sub={`${live.lastFrameAt && Date.now() - live.lastFrameAt < 2000 ? 'live' : 'idle'}`}
              mono
              divider
            />
            <Metric
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>}
              label="BYTES RECEIVED"
              value={fmtBytes(bytesEstimate)}
              sub="raw POST bodies"
              mono
              divider
            />
            <Metric
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /></svg>}
              label="VERDICTS SIGNED"
              value={verdictsSigned.toString()}
              sub="EC P-256 attested"
              mono
              divider
            />
            <Metric
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="1.6" /><circle cx="18" cy="6" r="1.6" /><circle cx="6" cy="18" r="1.6" /><circle cx="18" cy="18" r="1.6" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="6" y1="8" x2="6" y2="16" /><line x1="18" y1="8" x2="18" y2="16" /><line x1="8" y1="18" x2="16" y2="18" /></svg>}
              label="ACTIVE PILLARS"
              value={activeKindsCount.toString()}
              sub={activeKindsCount > 0 ? 'capturing' : 'idle'}
              mono
              divider
            />
            <Metric
              icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></svg>}
              label="FLEET"
              value={`${openSessions} / ${deviceSet.size || 1}d`}
              sub="open sessions / devices"
              mono
              divider
            />
          </div>
        </div>

        {/* CERTIFICATE PANEL — keeps mint/fetch logic, dark strip UI */}
        {id && (
          <CertificateCard sessionId={id} liveCertificate={live.certificate} autoMintTrigger={allDone} />
        )}

        {/* MAIN GRID */}
        <div className="sv-main-grid">
          {/* LEFT: PILLARS */}
          <div
            className="sv-scroll-panel xl:sticky xl:top-20 xl:self-start"
            style={{
              border: '1px solid rgba(120,160,160,0.16)',
              borderRadius: 14,
              background: 'rgba(9,16,26,0.55)',
              padding: '16px 14px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 2px 12px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6f878e" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect width="7" height="7" x="3" y="3" rx="1" />
                <rect width="7" height="7" x="14" y="3" rx="1" />
                <rect width="7" height="7" x="14" y="14" rx="1" />
                <rect width="7" height="7" x="3" y="14" rx="1" />
              </svg>
              <div>
                <div style={{ fontSize: 10, letterSpacing: 1.3, color: '#9fb3b8', fontWeight: 600 }}>
                  DIAGNOSTIC PILLARS
                </div>
                <div style={{ fontSize: 10, letterSpacing: 0.6, color: '#5d7178', marginTop: 2 }}>
                  {passed + concerns + failed} / {PILLARS.length} · RUNS ON-DEVICE + SERVER
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {PILLARS.map((p) => (
                <PillarTile
                  key={p.kind}
                  meta={p}
                  step={stepByKind.get(p.kind)}
                  frames={live.framesByKind[p.kind] ?? []}
                  selected={selected === p.kind}
                  onSelect={() => setSelected(p.kind)}
                />
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                borderTop: '1px solid rgba(120,160,160,0.1)',
                marginTop: 13,
                paddingTop: 13,
                color: '#9fb3b8',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
              <span style={{ fontSize: 11.5, letterSpacing: 0.5 }}>VIEW ALL PILLARS</span>
            </div>
          </div>

          {/* MIDDLE: selected pillar detail — scaled to fit the column so the
              fixed-width "1:1 design" panels shrink proportionally instead of
              overlapping when the centre column is narrower than DESIGN_WIDTH. */}
          <div className="sv-scroll-panel xl:sticky xl:top-20 xl:self-start">
            <ScaledPillarPanel
              meta={selectedMeta}
              step={stepByKind.get(selectedMeta.kind)}
              frames={live.framesByKind[selectedMeta.kind] ?? []}
              verdict={live.verdictByKind[selectedMeta.kind]}
            />
          </div>

          {/* RIGHT: REAL activity log */}
          <div className="sv-col-activity xl:sticky xl:top-20 xl:self-start">
            <ActivityLog lines={live.activity} />
          </div>
        </div>
      </div>
    </Layout>
  );
}

// DESIGN_WIDTH — the natural ("1:1 design") layout width of the pillar detail
// panels. Their internal children use fixed pixel widths that sum to roughly
// this; laying the inner box out at this width guarantees they never wrap or
// overlap, and the panel is then transform-scaled down to fit narrower columns.
// Single tunable constant: bump if a panel design grows wider than this.
const DESIGN_WIDTH = 1060;

function ScaledPillarPanel(props: React.ComponentProps<typeof PillarPanel>) {
  const { ref, innerRef, scale, contentHeight } = useScaleToFit(DESIGN_WIDTH);
  return (
    // OUTER measured wrapper: fills the centre column. Its height is set to the
    // scaled content height so the page reserves exactly the visible space (the
    // CSS transform doesn't shrink the layout box) — no empty gap, no clipping.
    <div
      ref={ref}
      style={{
        width: '100%',
        height: contentHeight,
        overflow: 'hidden',
      }}
    >
      {/* INNER box: always laid out at full DESIGN_WIDTH (so fixed-width
          children never collide), then scaled to fit. At scale === 1 this is a
          pixel-identical passthrough. */}
      <div
        ref={innerRef}
        style={{
          width: DESIGN_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <PillarPanel {...props} />
      </div>
    </div>
  );
}

function StatBox({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div style={{ padding: '0 24px', textAlign: 'center', borderRight: '1px solid rgba(120,160,160,0.12)' }}>
      <div style={{ fontSize: 30, fontWeight: 600, color, lineHeight: 1, letterSpacing: -1 }}>{value}</div>
      <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: '#6f878e', fontWeight: 600, marginTop: 6 }}>{label}</div>
    </div>
  );
}

function Metric({
  icon, label, value, sub, mono, divider,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  mono?: boolean;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '0 18px',
        borderLeft: divider ? '1px solid rgba(120,160,160,0.1)' : 'none',
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '1px solid rgba(20,222,205,0.25)',
          background: 'rgba(20,222,205,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#15ddc9',
        }}
      >
        {icon}
      </span>
      <div>
        <div style={{ fontSize: 9.5, letterSpacing: 1.3, color: '#6f878e', fontWeight: 600 }}>{label}</div>
        <div
          style={{
            fontSize: 19,
            fontWeight: 500,
            color: '#e8f2f1',
            marginTop: 5,
            letterSpacing: 0.5,
            fontFamily: mono ? "'JetBrains Mono', monospace" : 'Inter, sans-serif',
          }}
        >
          {value}
        </div>
        <div style={{ fontSize: 10.5, color: '#5d7178', marginTop: 3 }}>{sub}</div>
      </div>
    </div>
  );
}

function PhoneMock() {
  // Real photo of the device under test (Samsung Galaxy), resized to the
  // dashboard public/ folder. 48×99 keeps the image's native 0.483 portrait
  // aspect ratio so it fits the header without stretching. object-fit:contain
  // is a final guard against distortion if the source ratio ever changes.
  return (
    <img
      src="/samsung-device.png"
      alt="Samsung Galaxy — device under test"
      width={48}
      height={99}
      style={{
        width: 48,
        height: 99,
        objectFit: 'contain',
        borderRadius: 9,
        flexShrink: 0,
        display: 'block',
        border: '1px solid rgba(120,160,160,0.18)',
        boxShadow: '0 8px 20px -9px rgba(0,0,0,0.85)',
      }}
    />
  );
}

function msToUptime(ms: number): string {
  if (ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function relAgo(ts: number): string {
  const ms = Date.now() - ts;
  if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3_600_000)}h ago`;
}

function useTickingClock(): string {
  const [now, setNow] = useState(() => fmtClock(Date.now()));
  useEffect(() => {
    const h = setInterval(() => setNow(fmtClock(Date.now())), 87);
    return () => clearInterval(h);
  }, []);
  return now;
}
function fmtClock(t: number): string {
  const d = new Date(t);
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}
